/**
 * Carte d'affichage d'un client - Style harmonisé avec UsersCardsView
 */
import React, { memo } from 'react';
import { CLIENT_TYPE_CONFIG } from './clientTypes';

const ClientCard = memo(({
  client,
  isSelected,
  onToggleSelection,
  onEdit,
  onDelete
}) => {
  const typeConfig = CLIENT_TYPE_CONFIG[client.client_type] || CLIENT_TYPE_CONFIG.non_qualifie;

  return (
    <div
      style={{
        background: isSelected ? '#f0e6ff' : 'white',
        borderRadius: '16px',
        padding: 'clamp(16px, 4vw, 20px)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: isSelected ? '2px solid #6f42c1' : client.is_active ? '2px solid transparent' : '2px solid #dc3545',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
      onClick={() => onToggleSelection(client.id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
      }}
    >
      {/* Checkbox de sélection */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        width: '20px',
        height: '20px',
        borderRadius: '6px',
        border: `2px solid ${isSelected ? '#6f42c1' : '#dee2e6'}`,
        background: isSelected ? '#6f42c1' : 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {isSelected && '✓'}
      </div>

      {/* Ligne de badges */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '12px',
        marginLeft: '28px',
        alignItems: 'center'
      }}>
        {/* Badge Actif/Inactif */}
        <span style={{
          background: client.is_active
            ? 'linear-gradient(135deg, #28a745, #20c997)'
            : 'linear-gradient(135deg, #dc3545, #c82333)',
          color: 'white',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600'
        }}>
          {client.is_active ? '✅ Actif' : '❌ Inactif'}
        </span>

        {/* Badge Code */}
        <span style={{
          background: '#fff3cd',
          color: '#856404',
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600'
        }}>
          {client.code}
        </span>

        {/* Badge Type */}
        <span style={{
          background: typeConfig.bgColor,
          color: typeConfig.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: '600'
        }}>
          {typeConfig.label}
        </span>

        {/* Badge Géocodage */}
        {client.geocoding_status === 'error' && (
          <span style={{
            background: 'linear-gradient(135deg, #dc3545, #c82333)',
            color: 'white',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600'
          }} title={`Écart: ${client.geocoding_discrepancy_m}m`}>
            ⚠️ Adresse invalide
          </span>
        )}
        {client.geocoding_status === 'warning' && (
          <span style={{
            background: '#ffc107',
            color: '#212529',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600'
          }} title={`Écart: ${client.geocoding_discrepancy_m}m`}>
            ⚠️ À vérifier
          </span>
        )}
      </div>

      {/* Nom */}
      <h3 style={{
        margin: '0 0 12px 0',
        fontSize: 'clamp(16px, 3vw, 18px)',
        fontWeight: '600',
        color: '#333'
      }}>
        {client.name}
      </h3>

      {/* Informations */}
      <div style={{ marginBottom: '16px', fontSize: 'clamp(13px, 2.5vw, 14px)', flex: 1 }}>
        {client.city && (
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📍</span>
            <span style={{ color: '#666' }}>
              {client.city}{client.postal_code && ` (${client.postal_code})`}
            </span>
          </div>
        )}
        {client.contact_name && (
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>👤</span>
            <span style={{ color: '#666' }}>{client.contact_name}</span>
          </div>
        )}
        {client.contact_email && (
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📧</span>
            <span style={{ color: '#666', wordBreak: 'break-word' }}>{client.contact_email}</span>
          </div>
        )}
        {client.contact_phone && (
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📞</span>
            <span style={{ color: '#666' }}>{client.contact_phone}</span>
          </div>
        )}
        {client.prestation_count > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋</span>
            <span style={{ color: '#666' }}>{client.prestation_count} prestation(s)</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(client); }}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #007bff, #0056b3)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>✏️</span>
          <span>Modifier</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(client.id, client.name); }}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #dc3545, #c82333)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>🗑️</span>
          <span>Supprimer</span>
        </button>
      </div>
    </div>
  );
});

ClientCard.displayName = 'ClientCard';

export default ClientCard;
